import React from "react";
import PropTypes from "prop-types";

// Attention treatment per overall status — mirrors cost-control/ExecutiveSummary.
const ATTENTION = {
  red: { label: "Action needed", dot: "bg-danger", text: "text-danger" },
  amber: { label: "Needs attention", dot: "bg-warning", text: "text-warning" },
};

const CONFIDENCE_LABEL = { low: "Low", med: "Med", high: "High" };

const fmtEur = (n) =>
  new Intl.NumberFormat("en-IE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

// Sign-offs are auto-created on a hard breach, so the workflow count is simply the number of
// requests awaiting a decision in the queue.
function signoffAwaiting(summary) {
  return summary.approvals.count;
}

// Governance + workflow headline, e.g. "1 sign-off awaiting approval · 1 budget ungoverned".
function buildHeadline(summary) {
  const parts = [];
  const so = signoffAwaiting(summary);
  const ung = summary.governance.ungovernedCount;
  if (so > 0) parts.push(`${so} approval${so === 1 ? "" : "s"} pending`);
  if (ung > 0) parts.push(`${ung} budget${ung === 1 ? "" : "s"} ungoverned`);
  if (parts.length === 0 && summary.imminent.count > 0) {
    parts.push(`${summary.imminent.count} imminent breach${summary.imminent.count === 1 ? "" : "es"}`);
  }
  return parts.join(" · ");
}

// One-line posture prose naming the active governance/workflow streams.
function buildProse(summary) {
  const bits = [];
  if (signoffAwaiting(summary) > 0) bits.push("a hard cap was crossed and an approval is pending");
  const ung = summary.governance.ungovernedCount;
  if (ung > 0) {
    const sharePct = Math.round((1 - (summary.governance.coveragePct || 0)) * 100);
    bits.push(
      `${fmtEur(summary.governance.ungovernedSpendEur)} (${sharePct}%) of budgeted spend has no policy gating it`
    );
  }
  if (!bits.length) return "";
  const joined = bits.length > 1 ? `${bits.slice(0, -1).join(", ")}, and ${bits[bits.length - 1]}` : bits[0];
  return `${joined.charAt(0).toUpperCase()}${joined.slice(1)}.`;
}

// The CTA target depends on what's most pressing: the sign-off queue, else the budgets table.
function ctaFor(summary) {
  if (signoffAwaiting(summary) > 0) return { label: "Review approvals", target: "signoffs" };
  if (summary.governance.ungovernedCount > 0 || summary.imminent.count > 0) {
    return { label: "Review budgets", target: "budgets" };
  }
  return null;
}

function ConfidencePill({ confidence }) {
  if (!confidence) return null;
  const level = CONFIDENCE_LABEL[confidence.level] || confidence.level;
  return (
    <div className="shrink-0 rounded-full bg-side px-2.5 py-0.5 text-[11px] font-medium text-text-2">
      Breach forecast confidence: {level} · ±{Math.round(confidence.recent_error_pct)}%
    </div>
  );
}
ConfidencePill.propTypes = {
  confidence: PropTypes.shape({ level: PropTypes.string, recent_error_pct: PropTypes.number }),
};

// EnforcementVerdict — the posture banner. It leads with GOVERNANCE + WORKFLOW (sign-offs +
// ungoverned coverage), not spend, so the page reads as the guardrail console rather than a
// second spend dashboard. The product cannot block spend — prevention is human sign-off — so
// the copy never implies spend was stopped.
export default function EnforcementVerdict({ summary, onReview }) {
  const attn = ATTENTION[summary.status]; // undefined when green
  const cta = ctaFor(summary);

  return (
    <div className="rounded-card border border-card-bd bg-card px-5 py-4">
      <div className="mb-2 flex items-center justify-between gap-3">
        {attn ? (
          <div className={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide ${attn.text}`}>
            <span className={`inline-block rounded-full ${attn.dot}`} style={{ width: 8, height: 8 }} />
            {attn.label}
          </div>
        ) : (
          <div className="text-xs uppercase tracking-wide text-text-2">Executive summary</div>
        )}
        <ConfidencePill confidence={summary.confidence} />
      </div>

      {attn ? (
        <>
          <p className="text-sm font-semibold leading-snug text-text">{buildHeadline(summary)}</p>
          <p className="mt-1.5 text-sm leading-relaxed text-text-2">{buildProse(summary)}</p>
          {cta && onReview ? (
            <button
              type="button"
              onClick={() => onReview(cta.target)}
              className="mt-2.5 text-sm font-semibold text-primary hover:text-primary-hover"
            >
              {cta.label} →
            </button>
          ) : null}
        </>
      ) : (
        <p className="text-sm text-text-2">
          All clear — every budget is governed by a policy and nothing is awaiting your approval.
        </p>
      )}
    </div>
  );
}

EnforcementVerdict.propTypes = {
  summary: PropTypes.shape({
    status: PropTypes.oneOf(["red", "amber", "green"]).isRequired,
    confidence: PropTypes.shape({ level: PropTypes.string, recent_error_pct: PropTypes.number }),
    breaching: PropTypes.shape({ count: PropTypes.number }),
    imminent: PropTypes.shape({ count: PropTypes.number }),
    approvals: PropTypes.shape({ count: PropTypes.number }),
    governance: PropTypes.shape({
      ungovernedCount: PropTypes.number,
      ungovernedSpendEur: PropTypes.number,
      coveragePct: PropTypes.number,
    }),
  }).isRequired,
  onReview: PropTypes.func,
};
